import { currentBridgeData } from '@databases/database-state/state/bridge-data';

import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';

type PromptAPIOptions = {
  systemPrompt?: string;
  model?: string;
  tokenLimit?: number;
};

const DEFAULT_PROMPT_OPTIONS: Required<PromptAPIOptions> = {
  systemPrompt: '',
  model: 'anthropic.claude-sonnet-4-20250514-v1:0',
  tokenLimit: 10_000
} as const;

const UPDATED_MODEL = 'anthropic.claude-sonnet-4-6';

const SYSTEM_PROMPT_STORAGE_KEY_CREATE = 'databases-aifc-system-prompt-create';
const SYSTEM_PROMPT_STORAGE_KEY_EDIT = 'databases-aifc-system-prompt-edit';

const PromptStorage = {
  get(isEditMode: boolean): string {
    const key = isEditMode
      ? SYSTEM_PROMPT_STORAGE_KEY_EDIT
      : SYSTEM_PROMPT_STORAGE_KEY_CREATE;
    try {
      const stored = localStorage.getItem(key);
      return stored || '';
    } catch (error) {
      console.warn(
        'Failed to read system prompt from localStorage:',
        error,
        'falling back to empty string.'
      );
      return '';
    }
  },

  set(prompt: string, isEditMode: boolean): void {
    const key = isEditMode
      ? SYSTEM_PROMPT_STORAGE_KEY_EDIT
      : SYSTEM_PROMPT_STORAGE_KEY_CREATE;
    try {
      localStorage.setItem(key, prompt);
    } catch (error) {
      console.error('Failed to save system prompt to localStorage:', error);
    }
  },

  reset(isEditMode: boolean): void {
    const key = isEditMode
      ? SYSTEM_PROMPT_STORAGE_KEY_EDIT
      : SYSTEM_PROMPT_STORAGE_KEY_CREATE;
    try {
      localStorage.removeItem(key);
    } catch (error) {
      console.error('Failed to reset system prompt in localStorage:', error);
    }
  }
};

export class DatabasesPromptAPI {
  async generate(
    userPrompt: string,
    options: PromptAPIOptions & { isEditMode?: boolean } = {}
  ) {
    const { isEditMode = false, ...promptOptions } = options;
    const localSystemPrompt = PromptStorage.get(isEditMode);
    const { systemPrompt, tokenLimit, model } = {
      ...DEFAULT_PROMPT_OPTIONS,
      ...(isFeatureFlagEnabled('whiteboardsUpdateModels')
        ? { model: UPDATED_MODEL }
        : {}),
      ...promptOptions,
      ...(localSystemPrompt !== '' ? { systemPrompt: localSystemPrompt } : {})
    };

    const bridgeData = currentBridgeData.data;
    const accountId = bridgeData.identity.accountId;
    const tenantId = bridgeData.cloudId;

    if (!accountId || !tenantId) {
      throw new Error('Missing accountId or cloudId from bridge data');
    }

    const requestBody = {
      anthropic_version: 'bedrock-2023-05-31',
      system: systemPrompt,
      max_tokens: tokenLimit,
      messages: [
        {
          role: 'user',
          content: [
            {
              type: 'text',
              text: userPrompt
            }
          ]
        }
      ]
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'text/event-stream',
        'X-Atlassian-CloudId': tenantId,
        'X-Atlassian-UseCaseId': 'confluence-aifc',
        'X-Atlassian-UserId': accountId
      },
      body: JSON.stringify(requestBody)
    };

    const url = `/local/ai-gateway/v1/bedrock/model/${model}/invoke-with-response-stream`;
    const res = await fetch(url, requestOptions);

    if (res.status >= 400) {
      throw new Error(`HTTP ${res.status}: ${res.status} - ${res.statusText}`);
    }

    if (!res.body) {
      throw new Error('No response body in streaming response');
    }

    return res;
  }

  static getSystemPrompt(isEditMode = false) {
    return PromptStorage.get(isEditMode);
  }

  static setSystemPrompt(prompt: string, isEditMode = false) {
    PromptStorage.set(prompt, isEditMode);
  }

  static resetSystemPrompt(isEditMode = false) {
    PromptStorage.reset(isEditMode);
  }
}
